import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CalendarCheck, Loader2, AlertCircle, CheckCircle2,
  MapPin, ShieldCheck, ShieldAlert, Lock, LogIn,
} from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";

/**
 * Public confirmation of an event access card.
 *
 * Where a signed-out visitor lands when they scan the QR on a card. It answers
 * one question — is this card real, and does it admit the person holding it —
 * and offers nothing to press. Checking a delegate in is the desk's job, and
 * the desk signs in; scanning the same QR while signed in goes to /scan, which
 * is the page with the buttons.
 *
 * Fetched with a plain `fetch` rather than through the RTK api slice on
 * purpose: that slice attaches the stored bearer token and force-redirects to
 * /login on any 401. Neither belongs on a page whose whole point is working
 * without a session.
 */

type VerifyData = {
  registration_no: string | null;
  attendee_name: string | null;
  attendee_email: string | null;
  membership_no: string | null;
  photo_url: string | null;
  event: {
    title: string | null;
    location: string | null;
    start_date: string | null;
    end_date: string | null;
  };
  payment_status: string | null;
  is_settled: boolean;
  overall_attendance: string | null;
  days: Array<{ event_day_id: number; day_label: string; day_date: string | null; is_present: boolean }>;
  days_total: number;
  days_present: number;
  admits: boolean;
};

function fmt(d?: string | null) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:py-12">
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center">
          <div className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400">
            NIQS
          </div>
          <div className="text-xs font-bold text-gray-400 mt-1">
            Event access card verification
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function PublicRegistrationVerifyPage() {
  const { barcode } = useParams<{ barcode: string }>();

  const [data, setData] = useState<VerifyData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [photoBroken, setPhotoBroken] = useState(false);

  const load = useCallback(async () => {
    if (!barcode) return;
    setLoading(true);
    setError("");
    setPhotoBroken(false);
    try {
      const res = await fetch(`${BASE_URL}/events/verify/${encodeURIComponent(barcode)}`, {
        headers: { Accept: "application/json" },
      });
      const body = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(
          body?.message ||
            (res.status === 429
              ? "Too many checks from this device. Wait a moment and try again."
              : "Could not verify this card. Please try again."),
        );
      }
      setData(body?.data ?? null);
    } catch (e: any) {
      setError(e?.message || "Could not reach the verification service.");
    } finally {
      setLoading(false);
    }
  }, [barcode]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Shell>
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-10 text-center space-y-3">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto" />
          <p className="text-sm font-bold text-gray-500">Checking this card…</p>
        </div>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell>
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-rose-500" />
          </div>
          <h1 className="font-black text-gray-900 text-lg">Card not recognised</h1>
          <p className="text-sm text-gray-500 font-medium">{error}</p>
          <button
            onClick={load}
            className="w-full bg-gray-900 text-white font-black text-sm py-3 rounded-2xl hover:bg-gray-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      </Shell>
    );
  }

  // Two different "no": a card that is not real, and a real card whose fee is
  // unpaid. The gate has to be able to tell them apart — the second one is
  // fixed at the secretariat's desk in a minute, the first one is not fixed.
  const admits = !!data.admits;

  return (
    <Shell>
      {/* Verdict — the whole answer, before any detail. */}
      <div
        className={`rounded-3xl p-6 text-center shadow-sm border ${
          admits
            ? "bg-emerald-600 border-emerald-600 text-white"
            : "bg-amber-50 border-amber-200 text-amber-900"
        }`}
      >
        <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
          {admits
            ? <ShieldCheck className="w-8 h-8 text-white" />
            : <ShieldAlert className="w-8 h-8 text-amber-600" />}
        </div>
        <div className="font-black text-lg leading-tight">
          {admits ? "Valid access card" : "Registered — fee outstanding"}
        </div>
        <p className={`text-xs font-bold mt-1.5 ${admits ? "text-white/70" : "text-amber-700"}`}>
          {admits
            ? "This registration is confirmed and admits the holder."
            : "This is a genuine registration, but the fee has not been settled. The secretariat must record the payment before the holder is admitted."}
        </p>
      </div>

      {/* Holder */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-4">
          {/* The photograph is what makes a card checkable by eye — a QR only
              proves a card was issued, not who is carrying it. */}
          {data.photo_url && !photoBroken ? (
            <img
              src={data.photo_url}
              alt={data.attendee_name ?? "Attendee"}
              onError={() => setPhotoBroken(true)}
              className="w-16 h-16 rounded-2xl object-cover shrink-0 border-2 border-indigo-100"
            />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-indigo-500 flex items-center justify-center text-white font-black text-lg shrink-0">
              {data.attendee_name?.substring(0, 2).toUpperCase() ?? "—"}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-black text-gray-900 leading-snug break-words">
              {data.attendee_name ?? "—"}
            </div>
            {data.membership_no && (
              <div className="text-[11px] font-black text-indigo-600 uppercase tracking-widest mt-0.5">
                {data.membership_no}
              </div>
            )}
            {data.attendee_email && (
              <div className="text-xs font-medium text-gray-400 break-words">{data.attendee_email}</div>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-gray-50">
          <div className="min-w-0">
            <dt className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Registration</dt>
            <dd className="text-sm font-bold text-gray-800 break-words">{data.registration_no ?? "—"}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Fee</dt>
            <dd className={`text-sm font-bold capitalize ${data.is_settled ? "text-emerald-600" : "text-amber-600"}`}>
              {data.payment_status ?? "—"}
            </dd>
          </div>
        </dl>
      </div>

      {/* Event */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-2">
        <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Event</div>
        <div className="font-black text-gray-900 break-words">{data.event?.title ?? "—"}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium text-gray-500">
          {(data.event?.start_date || data.event?.end_date) && (
            <span className="flex items-center gap-1.5">
              <CalendarCheck className="w-3.5 h-3.5 shrink-0" />
              {fmt(data.event.start_date)}
              {data.event.end_date && data.event.end_date !== data.event.start_date
                ? ` – ${fmt(data.event.end_date)}`
                : ""}
            </span>
          )}
          {data.event?.location && (
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 shrink-0" /> {data.event.location}
            </span>
          )}
        </div>
      </div>

      {/* Attendance so far — shown, never changed. */}
      {data.days_total > 0 && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-50">
            <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Attendance</div>
            <div className="text-xs font-black text-gray-500">
              {data.days_present}/{data.days_total} present
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {data.days.map((day) => (
              <div
                key={day.event_day_id}
                className={`flex items-center justify-between gap-3 px-5 py-3.5 ${day.is_present ? "bg-emerald-50" : "bg-white"}`}
              >
                <div className="min-w-0">
                  <div className={`text-sm font-bold ${day.is_present ? "text-emerald-800" : "text-gray-700"}`}>
                    {day.day_label}
                  </div>
                  <div className="text-xs text-gray-400 font-medium">{fmt(day.day_date)}</div>
                </div>
                <span
                  className={`shrink-0 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border ${
                    day.is_present
                      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : "bg-gray-50 text-gray-400 border-gray-100"
                  }`}
                >
                  {day.is_present && <CheckCircle2 className="w-3.5 h-3.5" />}
                  {day.is_present ? "Present" : "Not yet"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Why there is nothing to press. */}
      <div className="bg-gray-900 rounded-3xl p-5 text-white space-y-3">
        <div className="flex items-start gap-3">
          <Lock className="w-4 h-4 shrink-0 mt-0.5 text-white/40" />
          <p className="text-xs font-bold text-white/70 leading-relaxed">
            This is a view-only check. Marking a delegate present, issuing meals
            and handing out materials are done by the secretariat from a signed-in
            device.
          </p>
        </div>
        {/* `from` is the shape LoginPage reads on success, so signing in from
            here lands back on the desk scanner for this same card. */}
        <Link
          to="/login"
          state={{ from: { pathname: `/scan/${barcode ?? ""}` } }}
          className="flex items-center justify-center gap-2 w-full bg-white/10 hover:bg-white/20 transition-colors text-white font-black text-xs uppercase tracking-widest py-3 rounded-2xl"
        >
          <LogIn className="w-3.5 h-3.5" /> Sign in to check in
        </Link>
      </div>
    </Shell>
  );
}
